<template>
    <div v-if="rows.length" class="transfer-details">
        <ul>
            <li v-for="(row, index) in rows" :key="index">
                <strong>{{ row.key }}</strong>
                <span v-suppress="row.sensitive">{{ row.value }}</span>
            </li>
        </ul>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType } from 'vue';
    import { convertToDate } from '@shared/helpers/datetime/datetime';
    import { TransferJourneyType } from '@/helpers/transfer/transfer.interfaces';
    import { TransferProduct } from '@/api/Cart/cart.interfaces';
    import { isNotNil } from '@shared/utils/value';
    import { getLocaleDigitDateTime } from '@shared/helpers/datetime/datetime.formatters';
    import { getStoredTransferTrip } from '@/helpers/transfer/transfer';

    interface DetailsRow {
        key: string;
        value: any;
        sensitive?: boolean;
    }

    export default defineComponent({
        name: 'TransferDetails',
        methods: { t },
        props: {
            transfer: {
                type: Object as PropType<TransferProduct>,
                required: true,
            },
            showShieldText: { type: Boolean, default: true },
        },
        setup(props) {
            const trip = computed(() => getStoredTransferTrip(props?.transfer?.TripId ?? ''));

            const getUnconfirmedPickupLabelKey = () => {
                const isPrivateTransfer = !props.transfer.IsSharedTransfer;

                if (isPrivateTransfer) {
                    switch (props.transfer.TransferType) {
                        case TransferJourneyType.FlightHotel:
                            return 'shared.pickup_time_unconfirmed_private_FlightHotel';
                        case TransferJourneyType.HotelFlight:
                            return 'shared.pickup_time_unconfirmed_private_HotelFlight';
                    }
                }
                return 'shared.pickup_time_unconfirmed_default';
            };

            const rows = computed<DetailsRow[]>(() => {
                if (!trip.value) {
                    return [];
                }

                const { from, to, request } = trip.value;

                const pickupTimeDate = request?.startTime
                    ? convertToDate(request.startTime)
                    : null;

                const isUnconfirmedPickupTime = !pickupTimeDate;

                const flightArrivalTimeDate = request?.flightArrivalTime
                    ? convertToDate(request.flightArrivalTime)
                    : null;

                const flightDepartureTimeDate = request?.flightDepartureTime
                    ? convertToDate(request.flightDepartureTime)
                    : null;

                return [
                    {
                        key: t({ key: 'shared.pickup_location' }),
                        value: from.description,
                    },
                    flightArrivalTimeDate && {
                        key: t({
                            key: 'shared.flight_arrival_time',
                        }),
                        value: getLocaleDigitDateTime(flightArrivalTimeDate),
                    },
                    pickupTimeDate && {
                        key: t({ key: 'shared.pickup_time' }),
                        value: getLocaleDigitDateTime(pickupTimeDate),
                    },
                    isUnconfirmedPickupTime && {
                        key: t({ key: 'shared.pickup_time' }),
                        value: t({
                            key: getUnconfirmedPickupLabelKey(),
                        }),
                    },
                    {
                        key: t({ key: 'shared.dropoff_location' }),
                        value: to.description,
                    },
                    flightDepartureTimeDate && {
                        key: t({
                            key: 'shared.flight_departure_time',
                        }),
                        value: getLocaleDigitDateTime(flightDepartureTimeDate),
                    },
                    props.showShieldText &&
                        props.transfer?.ShieldText && {
                            key: t({ key: 'shared.name_sign' }),
                            value: props.transfer.ShieldText,
                            sensitive: true,
                        },
                ]
                    .filter((x) => x)
                    .filter(isNotNil) as DetailsRow[];
            });

            return { rows };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'TransferDetails';
</style>
